import { IBannerInlinePlaceholderProps } from '@tekface/smart-activation';
import {
  DEFAULT_RETURN_FILTERABLE,
  EXTENDED_RETURN_FILTERABLE,
  SearchProductFilter,
  SearchV2Params,
} from '@teko/discovery/v2';
import {
  Body,
  Col,
  Container,
  ContainerProps,
  Flex,
  Row,
  Shape,
  SubTitle,
  Title,
  styled,
} from '@teko/ds-desktop';
import { ForwardOutlined } from '@teko/ds-icon';
import {
  BannerEntity,
  SelectionOption,
  VerticalFilter,
} from '@teko/teko-ecommerce';
import { trackCustomEvent, trackViewProductList } from '@teko/tracking';
import { SizeEnum } from '@teko/types/ds';
import {
  CategoriesFilterOption,
  ProductCardProps,
} from '@teko/types/ecommerce';
import {
  cleanObject,
  scheduleTaskInIdleTime,
  stringToInteger,
} from '@teko/utils';
import { InlineBanner } from 'components/InlineBanner';
import { useScreen } from 'contexts';
import {
  buildTreeCategoriesFilter,
  convertDiscoveryFilter,
  convertToPayloadFilter,
  convertToPayloadSorting,
} from 'helpers';
import { usePos365StoreCode, useSearch, useTenantConfig } from 'hooks';
import { useCachedProfileId } from 'hooks/useCachedProfileId';
import { useSearchAlgo } from 'hooks/useSearchAlgo';
import { t } from 'i18n';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { ParsedUrlQueryInput } from 'querystring';
import { useEffect, useMemo, useState } from 'react';
import {
  INLINE_BANNER_CLASSNAME,
  ITEM_PER_PAGE_IN_LISTING_PAGE,
} from '../../../constants';
import { useFilter } from '../hooks/useFilter';
import {
  convertSubsections,
  cookFilterPrice,
  deriveListProducts,
  extractSortQuery,
  getActiveSortOption,
  getCollectionSlug,
  getDefaultSortOptions,
  getDisplayFilter,
  getSignatureQueryParam,
} from '../shared/helpers';
import { ShowMoreButton } from '../shared/styles';
import { FeaturedProductsProps, SeoInfo } from '../shared/types';

const SubSection = dynamic(() =>
  import('../shared/SubSection').then((mod) => mod.SubSection),
);

const NoProductsFound = dynamic(() =>
  import('@teko/teko-ecommerce').then((mod) => mod.NoProductsFound),
);

const ProductList = dynamic(() =>
  import('@teko/teko-ecommerce').then((mod) => mod.ProductList),
);

const ProductGrid = dynamic(() =>
  import('@teko/teko-ecommerce').then((mod) => mod.ProductGrid),
);

const DiscoveryRecommendedProducts = dynamic(() =>
  import('components/DiscoveryRecommendedProducts').then(
    (mod) => mod.DiscoveryRecommendedProducts,
  ),
);

const ProductCarouselWithHeader = dynamic(() =>
  import('@teko/teko-ecommerce').then((mod) => mod.ProductCarouselWithHeader),
);

const BannerSlides = dynamic(() =>
  import('components/desktop/organisms/BannerSlides').then(
    (mod) => mod.BannerSlides,
  ),
);

const NUMBER_OF_FEATURED_PRODUCTS_PER_ROW = 5;

const BANNER_SLIDERS_IMAGE_HEIGHT = 250;

interface ProductListWithFilterProps extends Omit<ContainerProps, 'children'> {
  siteTitle?: string;
  serverFilters?: SearchProductFilter;
  serverProducts?: ProductCardProps[];
  serverPagination?: {
    totalItems: number;
    totalPages: number;
  };
  filterPrice?: {
    max: number;
    min: number;
  };
  serverSeoInfo?: SeoInfo;
  customerParams?: ParsedUrlQueryInput;
  featuredProducts?: FeaturedProductsProps;
  inlineBanners?: IBannerInlinePlaceholderProps<{}>['banners'];
}

export const ProductListWithFilter = ({
  siteTitle,
  serverProducts,
  serverFilters = {},
  serverPagination,
  filterPrice,
  serverSeoInfo,
  customerParams,
  featuredProducts,
  inlineBanners,
  ...props
}: ProductListWithFilterProps) => {
  const router = useRouter();
  const { query, asPath } = router;
  const {
    showCollectionPageTitle,
    defaultSortOptionCode,
    addSchemaItemList,
    baseDomain,
    meta,
    sortByCreatedAt,
    displayFilterTypeCategory,
    subSections,
    contactLink,
  } = useTenantConfig();
  let clientFilterPrice = filterPrice;
  const [newQuery, setNewQuery] = useState<ParsedUrlQueryInput>(query);
  const {
    clearFilterLink,
    getActiveFilterOptions,
    onClickFilterOption,
    onSortByPrice,
    onSortOptionSelected,
    getActiveCategories,
    onClickCategoriesFilterOption,
    onRemoveCategory,
  } = useFilter(newQuery, setNewQuery, { autoSubmit: true });
  const { getSearchAlgoFromCookie, getDefaultSortOption } = useSearchAlgo();
  const { trackingScreenName } = useScreen();

  const cachedProfileId = useCachedProfileId();

  let filters = serverFilters;

  const { pos365StoreCode } = usePos365StoreCode();
  const slug = getCollectionSlug(query.slug as string);
  const { providerId, sellerId, brandId, brands } = query;
  // to avoid sending duplicate GTM events
  const [, setProductSkuList] = useState<string[]>([]);

  const bannerClassname = slug
    ? INLINE_BANNER_CLASSNAME.INLINE_CATEGORY
    : brandId
    ? INLINE_BANNER_CLASSNAME.INLINE_BRAND
    : sellerId
    ? INLINE_BANNER_CLASSNAME.INLINE_SELLER
    : trackingScreenName === 'searchProductResult'
    ? INLINE_BANNER_CLASSNAME.INLINE_SEARCH
    : null;

  const [queryPage, setPage] = useState(parseInt(query?.page as string) || 1);
  const [displayProducts, setDisplayProducts] = useState<ProductCardProps[]>(
    serverProducts || [],
  );

  // sort
  const activeSortOption = getActiveSortOption(
    query,
    sortByCreatedAt,
    getDefaultSortOption(defaultSortOptionCode),
  );
  const [sort, order] = activeSortOption
    ? extractSortQuery(activeSortOption.optionCode)
    : [undefined, undefined];
  const payloadFilters = {
    // brandId --> case brand page, example: /asus-brand.asus
    // brands --> case filter brand: example: /search?router=productListing&query=áo&brands=aimer%2Cbare
    brands: brandId
      ? typeof brandId === 'string'
        ? [brandId]
        : [...brandId]
      : brands,
    // seller page: /*-sel.:sellerId
    sellerIds: sellerId && [sellerId],
    // provider page: /cua-hang/335?pageTitle=Takashimaya%20Official%20Store
    providerIds: providerId && [providerId],
    // for .cat and .scat page
    ...customerParams,
    // other query --> for search page filter
    ...query,
  };

  const { isLoading, result, transformProduct } = useSearch(
    cleanObject({
      page: queryPage,
      pageSize: ITEM_PER_PAGE_IN_LISTING_PAGE,
      slug,
      query: query.query,
      filter: convertToPayloadFilter(payloadFilters),
      sorting: convertToPayloadSorting(sort, order),
      returnFilterable: [],
      isNeedFeaturedProducts: !!slug,
      userId: cachedProfileId,
    }) as SearchV2Params,
    {
      products: serverProducts,
      pagination: serverPagination,
      filters: {} as SearchProductFilter,
      seoInfo: serverSeoInfo,
    },
    {
      onSuccess: ({ products }: { products: ProductCardProps[] }) => {
        const isFirstPage =
          queryPage === (parseInt(query?.page as string) || 1);
        setDisplayProducts((current) => [
          ...(isFirstPage ? [] : current),
          ...products,
        ]);
      },
    },
    getSearchAlgoFromCookie(),
  );

  const { pagination, products, seoInfo } = result || {};
  const { totalItems, totalPages } = pagination || {};

  const hasMoreProducts =
    (totalItems || 0) > (queryPage || 1) * ITEM_PER_PAGE_IN_LISTING_PAGE;

  const listProducts = useMemo(
    () =>
      deriveListProducts(
        queryPage,
        displayProducts,
        result?.products,
        transformProduct,
      ),
    [queryPage, displayProducts, result?.products, transformProduct],
  );

  const { result: filterResult } = useSearch(
    // currently, only serverside render filter for collection and brand page
    !slug && !brandId
      ? (cleanObject({
          slug,
          query: query.query,
          filter: !query.query
            ? // call discovery search to get products
              convertToPayloadFilter(getSignatureQueryParam(query))
            : {},
          returnFilterable: displayFilterTypeCategory
            ? EXTENDED_RETURN_FILTERABLE
            : DEFAULT_RETURN_FILTERABLE,
          pageSize: 1,
          userId: cachedProfileId,
        }) as SearchV2Params)
      : {},
    {
      products: [],
      pagination: serverPagination,
      filters: serverFilters,
    },
    undefined,
    getSearchAlgoFromCookie(),
  );
  // currently, only serverside render filter for collection and brand page
  if (!slug && !brandId) {
    clientFilterPrice = {
      max: (filterResult?.filters?.priceLte as number) || 10000000,
      min: (filterResult?.filters?.priceGte as number) || 0,
    };
    filters = filterResult?.filters as SearchProductFilter;
  }

  const categoriesFilters = buildTreeCategoriesFilter(filters.categories || []);

  useEffect(() => {
    // reset page on query change
    setPage(parseInt(query?.page as string) || 1);
  }, [query]);

  const searchResultSkuSignature = (products ?? [])
    .map((product) => product.sku)
    .join(',');
  useEffect(() => {
    if (isLoading || !query.query?.length || queryPage !== 1) return;
    trackCustomEvent('siteSearchResult', {
      action: 'search',
      category: getSearchAlgoFromCookie() || undefined,
      label: query.query.toString(),
      property: JSON.stringify({
        products: (products ?? []).map((product) => product.sku),
        filter: convertToPayloadFilter(payloadFilters),
      }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResultSkuSignature, isLoading, query.query, queryPage]);

  // send GTM track view product list event
  useEffect(() => {
    scheduleTaskInIdleTime(() => {
      setProductSkuList((prevSkuList) => {
        const newSkuList = products?.map((product) => product.sku || '') ?? [];

        // check if these 2 arrays are different
        const different =
          prevSkuList.length !== newSkuList?.length ||
          prevSkuList.find((sku) => !newSkuList.includes(sku));

        if (different) {
          trackViewProductList(
            products?.map((product) => ({
              sku: product.sku || '',
            })) ?? [],
          );
          return newSkuList;
        }
        return prevSkuList;
      });
    }, 3000);
  }, [products]);

  // filter price
  const { price_gte = '', price_lte = '' } = query;
  const { priceGte, priceLte, priceFilter } = useMemo(() => {
    const priceFilter = cookFilterPrice(clientFilterPrice?.max);
    return {
      priceGte: price_gte
        ? stringToInteger(price_gte.toString())
        : priceFilter.min,
      priceLte: price_lte
        ? stringToInteger(price_lte.toString())
        : priceFilter.max,
      priceFilter,
    };
  }, [price_gte, price_lte, clientFilterPrice?.max]);

  // PageAbove contain: page title, bannersliders, subsection
  const renderPageAbove = () => {
    const showPageTitle = !!showCollectionPageTitle && !!siteTitle;
    const subSectionFromWorkload = subSections?.[slug]
      ? convertSubsections(subSections?.[slug])
      : [];
    const subsectionsData = !!seoInfo?.subsections?.length
      ? seoInfo?.subsections
      : subSectionFromWorkload;

    const desktopBannerSliders = seoInfo?.bannerSliders?.find(
      ({ type }) => type === 'DESKTOP',
    );

    const showPageAbove =
      showPageTitle ||
      !!subsectionsData?.length ||
      !!desktopBannerSliders?.items?.length;

    if (!showPageAbove) return null;
    return (
      <Shape
        backgroundColor="white"
        padding={['s12', 's16']}
        marginBottom={!featuredProducts?.products?.length ? 's4' : 's0'}
      >
        {showPageTitle && (
          <PageTitleContainer justify="start" align="baseline" borderRadius="m">
            <PageTitle>{siteTitle}</PageTitle>
            {totalItems != undefined && (
              <Title color="textSecondary" paddingLeft="s8" weight="regular">
                {t('({{quantity}} sản phẩm)', {
                  quantity: totalItems,
                })}
              </Title>
            )}
          </PageTitleContainer>
        )}
        {!!desktopBannerSliders?.items?.length && (
          <StyledBannerSlidesWrapper marginTop={showPageTitle ? 's8' : 's0'}>
            <BannerSlides
              banners={desktopBannerSliders?.items.map(
                (item, index) =>
                  ({
                    id: `${index}${item.linkUrl}`,
                    targetPath: item.linkUrl,
                    image: item.thumbnailUrl,
                  } as BannerEntity),
              )}
              prioritized={true}
              showControl={true}
              imageHeight={BANNER_SLIDERS_IMAGE_HEIGHT}
            />
          </StyledBannerSlidesWrapper>
        )}
        {!!subsectionsData?.length && (
          <SubSection subsections={subsectionsData} />
        )}
      </Shape>
    );
  };

  // render featured products block
  const renderFeaturedProducts = () => {
    if (!featuredProducts?.products?.length) return null;
    return (
      <Shape marginBottom="s24" backgroundColor="white" padding="s16">
        <ProductCarouselWithHeader
          header={
            featuredProducts.blockTitle
              ? {
                  title: featuredProducts.blockTitle,
                  titleColor: 'white',
                  style: { justifyContent: 'center' },
                }
              : undefined
          }
          productsPerRow={NUMBER_OF_FEATURED_PRODUCTS_PER_ROW}
          products={featuredProducts.products}
          background={featuredProducts.backgroundImageUrl}
          style={{
            borderRadius: '4px',
            // add top padding only when
            // - block title not exists
            // - block background image exists
            paddingTop:
              !!featuredProducts.blockTitle ||
              !featuredProducts.backgroundImageUrl
                ? '0px'
                : '3.5rem',
          }}
          backgroundPrioritize
        />
      </Shape>
    );
  };

  // render sort bar
  const renderSortBar = () => (
    <SortBarWrapper>
      <Flex justify="center" align="center" height={'100%'} paddingLeft="s16">
        <SubTitle marginRight="s32">{t('Sắp xếp theo')}</SubTitle>
        {getDefaultSortOptions(sortByCreatedAt).map((option, index: number) => (
          <SelectionOption
            key={index}
            iconActiveSize={SizeEnum.s}
            block={false}
            isActive={activeSortOption?.optionCode === option.optionCode}
            onClick={() => onSortOptionSelected(option)}
            style={{ padding: '0.5rem', marginRight: '1rem' }}
          >
            <Body level={1}>{t(option.optionName)}</Body>
          </SelectionOption>
        ))}
      </Flex>
    </SortBarWrapper>
  );

  // render product list
  const renderProductList = () => (
    <>
      {!listProducts?.length && !isLoading && (
        <>
          <StyledNoProductsFound show={!isLoading && !listProducts?.length} />
          <StyledContainer>
            <DiscoveryRecommendedProducts />
          </StyledContainer>
        </>
      )}
      {!!pos365StoreCode ? (
        <ProductList
          products={listProducts}
          regionName="itemProductResult"
          itemsPerPage={
            Math.ceil(totalItems / totalPages) || ITEM_PER_PAGE_IN_LISTING_PAGE
          }
          contactLink={contactLink}
        />
      ) : (
        <ProductGrid
          products={listProducts}
          regionName="itemProductResult"
          itemsPerPage={
            Math.ceil(totalItems / totalPages) || ITEM_PER_PAGE_IN_LISTING_PAGE
          }
          contactLink={contactLink}
        />
      )}
    </>
  );

  const renderViewMore = () => {
    if (!hasMoreProducts) {
      return null;
    }
    return (
      <Flex justify="center" marginBottom="s8" width="100%">
        <ShowMoreButton
          variant="link"
          suffix={<ForwardOutlined />}
          disabled={isLoading}
          onClick={(e: React.MouseEvent) => {
            setPage((page) => page + 1);
            e?.preventDefault();
          }}
        >
          {t('Xem thêm sản phẩm')}
        </ShowMoreButton>
      </Flex>
    );
  };

  const queryOmitSlug = useMemo(() => ({ ...query }), [query]);
  delete queryOmitSlug.slug;
  const productsSchema = useMemo(
    () =>
      !Object.keys(queryOmitSlug).length ||
      (Object.keys(queryOmitSlug).length === 1 &&
        Number(queryOmitSlug.page) === 1)
        ? listProducts
        : [],
    [queryOmitSlug, listProducts],
  );

  return (
    <>
      {addSchemaItemList && !!productsSchema?.length ? (
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'ItemList',
                name: serverSeoInfo?.siteTitle,
                url: `${baseDomain}${asPath}`,
                description: serverSeoInfo?.description || meta.description,
                itemListElement: productsSchema.map((product, index) => ({
                  '@type': 'ListItem',
                  position: index + 1,
                  item: {
                    name: product.name,
                    url:
                      baseDomain +
                      (product.link?.as as { pathname: string })?.pathname,
                  },
                })),
              }),
            }}
          />
        </Head>
      ) : null}
      <Container {...props}>
        <Row gutter={[16, 16]} marginBottom="s16">
          <Col span={2}>
            <VerticalFilter
              activeOptions={getActiveFilterOptions(
                convertDiscoveryFilter(filters, displayFilterTypeCategory),
              )}
              filters={getDisplayFilter(
                convertDiscoveryFilter(filters, displayFilterTypeCategory),
              )}
              onClickOption={onClickFilterOption}
              clearFilterLink={{
                href: clearFilterLink,
              }}
              filterPrice={
                priceFilter
                  ? {
                      max: priceFilter.max,
                      min: priceFilter.min,
                      step: priceFilter.step,
                      value: {
                        gte: priceGte,
                        lte: priceLte,
                      },
                      onFilter: onSortByPrice,
                    }
                  : undefined
              }
              // for categories filter
              categoriesFilter={
                categoriesFilters.length
                  ? {
                      filterName: 'Danh mục sản phẩm',
                      filterOptions: categoriesFilters,
                      onClickCategoryOption: (option: CategoriesFilterOption) =>
                        onClickCategoriesFilterOption(
                          option,
                          categoriesFilters,
                        ),
                      activeOptions: getActiveCategories(categoriesFilters),
                      onRemoveCategory: onRemoveCategory,
                    }
                  : undefined
              }
            />
          </Col>
          <Col span={10}>
            {bannerClassname ? (
              <InlineBanner
                inlineBanners={inlineBanners}
                inlineBannerClass={bannerClassname}
              />
            ) : null}
            {renderPageAbove()}
            {renderFeaturedProducts()}
            {
              <Shape backgroundColor="white" width="100%" borderRadius="m">
                {renderSortBar()}
                {renderProductList()}
              </Shape>
            }
            <Row marginTop="s16">{renderViewMore()}</Row>
          </Col>
        </Row>
      </Container>
    </>
  );
};

const PageTitleContainer = styled(Row)`
  background-color: ${({ theme }) => theme.colors.white};
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: 1.5rem;
  line-height: 2rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const SortBarWrapper = styled(Flex)`
  width: 100%;
  height: 64px;
  border-bottom: 1px solid #eaeaea;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  justify-content: space-between;
  font-size: 14px;
`;

const StyledNoProductsFound = styled(NoProductsFound)`
  margin-top: 4rem;
  margin-bottom: 4rem;
`;

const StyledContainer = styled(Container)`
  max-width: 100%;
  margin-bottom: 24px;
`;

const StyledBannerSlidesWrapper = styled(Shape)`
  .glide__bullets--override {
    bottom: 1.5rem !important;
  }
`;
